import { useEffect, useRef, useState } from 'react';
import { attachmentKind, type AttachmentKind } from '@goyone/shared';
import { api } from '../lib/api';
import { Icon, type IconName } from './Icon';

/**
 * معاينة المرفقات.
 *
 * القيد الحاكم: **الملف لا يُقرأ برابط مباشر**. تنزيله يحتاج ترويسة المستأجر
 * ورمز الوصول، وتنقّل المتصفح العادي لا يحملهما — فـ`<img src="/attachments/…">`
 * يصل إلى صفحة رفض لا إلى صورة. لذلك يُجلب الملف عبر `fetch` ويُلفّ في
 * `blob:` URL، وهو ما تفهمه الوسوم كلها.
 *
 * وعنوان الـblob مورد لا نصّ: كل واحد يحجز ذاكرة حتى يُبطَل صراحةً. تركُه
 * يتسرّب مع كل تصيير يُراكم عشرات الميجابايت في تبويب يُفتح طوال اليوم.
 */

/** أيقونة تدل على النوع بلا تحميل الملف. */
export function iconForKind(kind: AttachmentKind): IconName {
  switch (kind) {
    case 'image':
      return 'layers';
    case 'pdf':
      return 'receipt';
    case 'audio':
      return 'chat';
    default:
      return 'book';
  }
}

/**
 * عنوان blob للمرفق، يُجلب مرة ويُبطَل عند الخروج.
 *
 * `enabled` يؤجّل الجلب حتى يُحتاج فعلًا: صفحةٌ فيها عشرون مرفقًا لا تُنزّل
 * عشرين ملفًا لتعرض أيقوناتها.
 */
export function useAttachmentUrl(id: string, enabled: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let revoked = false;
    let created: string | null = null;

    setLoading(true);
    api<Blob>(`/attachments/${id}/download`, { raw: true })
      .then((blob) => {
        /*
         * الفحص قبل الإنشاء لا بعده.
         *
         * المكوّن قد يُزال بينما الطلب في الطريق. إنشاء العنوان ثم اكتشاف
         * ذلك يترك موردًا لا يملكه أحد — لا مرجع له يُبطله لاحقًا.
         */
        if (revoked) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch((e) => {
        if (!revoked) setError(e instanceof Error ? e.message : 'تعذّر تحميل الملف.');
      })
      .finally(() => {
        if (!revoked) setLoading(false);
      });

    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [id, enabled]);

  return { url, error, loading };
}

/**
 * هل دخل العنصر مجال الرؤية.
 *
 * الصور تُجلب عند ظهورها لا عند تصيير الصفحة: قائمة مرفقات طويلة كانت
 * ستُنزّل كل صورها دفعةً واحدة، وأكثرها تحت حافة الشاشة لا يراه أحد.
 */
function useInView<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;

    /* بيئات الاختبار وبعض المتصفحات القديمة بلا مراقب: تُعرض فورًا. */
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: '120px' },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [seen]);

  return [ref, seen];
}

/** مصغَّرة الصورة داخل بطاقة المرفق. */
export function ImageThumb({
  id,
  filename,
  onOpen,
}: {
  id: string;
  filename: string;
  onOpen: () => void;
}) {
  const [ref, inView] = useInView<HTMLButtonElement>();
  const { url, loading, error } = useAttachmentUrl(id, inView);

  return (
    <button
      ref={ref}
      type="button"
      className="att-thumb"
      onClick={onOpen}
      aria-label={`معاينة ${filename}`}
      title="اضغط للتكبير"
    >
      {url ? (
        <img src={url} alt="" loading="lazy" />
      ) : error ? (
        <Icon name="alert" size={18} />
      ) : loading || inView ? (
        <span className="skeleton" style={{ width: '100%', height: '100%' }} />
      ) : (
        <Icon name="layers" size={18} />
      )}
    </button>
  );
}

/**
 * مصغَّرة PDF: الصفحة الأولى فعلًا لا أيقونة صمّاء.
 *
 * عارض المتصفح نفسه في إطارٍ مصغَّرٍ مكبَّرٍ داخليًّا ثم مقلَّص — فلا
 * مكتبة PDF في المشروع، وهو مبدأ الطباعة والعارض ذاته. الإطار أصمّ
 * للنقر والزرّ حوله هو ما يفتح المعاينة.
 */
export function PdfThumb({
  id,
  filename,
  onOpen,
}: {
  id: string;
  filename: string;
  onOpen: () => void;
}) {
  const [ref, inView] = useInView<HTMLButtonElement>();
  const { url, loading, error } = useAttachmentUrl(id, inView);

  return (
    <button
      ref={ref}
      type="button"
      className="att-thumb att-thumb-pdf"
      onClick={onOpen}
      aria-label={`معاينة ${filename}`}
      title="اضغط للمعاينة"
    >
      {url ? (
        <iframe
          className="att-pdfthumb"
          src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          title=""
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : error ? (
        <Icon name="alert" size={18} />
      ) : loading || inView ? (
        <span className="skeleton" style={{ width: '100%', height: '100%' }} />
      ) : (
        <Icon name="receipt" size={18} />
      )}
    </button>
  );
}

/**
 * مشغّل صوت داخل الصفحة.
 *
 * التسجيل الصوتي يُسمَع حيث هو: تنزيله ثم فتحه في مشغّل خارجي يقطع القراءة
 * لدقيقة كاملة، ومن يراجع عشر مكالمات لا يفعلها.
 */
export function AudioPlayer({ id }: { id: string }) {
  const [armed, setArmed] = useState(false);
  const { url, loading, error } = useAttachmentUrl(id, armed);

  if (!armed) {
    return (
      <button type="button" className="btn btn-sm" onClick={() => setArmed(true)}>
        <Icon name="chat" size={14} />
        تشغيل
      </button>
    );
  }

  if (error) return <span className="tiny" style={{ color: 'var(--danger)' }}>{error}</span>;
  if (!url || loading) return <span className="skeleton" style={{ width: 180, height: 32 }} />;

  /* eslint-disable-next-line jsx-a11y/media-has-caption -- تسجيل مكالمة لا مادة منشورة. */
  return <audio className="att-audio" src={url} controls preload="metadata" />;
}

/**
 * عارض الصورة المكبَّرة.
 *
 * `Escape` يغلق، والنقر خارج الصورة يغلق. الصورة نفسها لا تغلق بالنقر:
 * من يكبّر صورة ليقرأ ختمًا فيها ينقر عليها ليقرّبها لا ليغلقها.
 */
export function Lightbox({
  id,
  filename,
  onClose,
}: {
  id: string;
  filename: string;
  onClose: () => void;
}) {
  const { url, loading, error } = useAttachmentUrl(id, true);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={filename}
      onClick={onClose}
    >
      <button className="lightbox-close btn btn-icon" onClick={onClose} aria-label="إغلاق">
        <Icon name="x" size={18} />
      </button>

      {/* ملء الشاشة على الصورة نفسها — والخروج بـEscape كما يعرفه الجميع. */}
      {url && (
        <button
          className="lightbox-close btn btn-icon"
          style={{ insetInlineEnd: 64 }}
          onClick={(e) => {
            e.stopPropagation();
            void imgRef.current?.requestFullscreen?.();
          }}
          aria-label="ملء الشاشة"
          title="ملء الشاشة"
        >
          <Icon name="expand" size={16} />
        </button>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="skeleton" style={{ width: 320, height: 220 }} />}
      {url && (
        <img
          ref={imgRef}
          className="lightbox-img"
          src={url}
          alt={filename}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      <div className="lightbox-name">{filename}</div>
    </div>
  );
}

export { attachmentKind };
